import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { FormGroup, ɵValue } from '@angular/forms';
import { Directive, OnDestroy } from '@angular/core';

@Directive()
export class FormState<T extends { [K in keyof T]: T[K] }> implements OnDestroy {
  private _form: FormGroup<T> = new FormGroup({} as T);
  private readonly initialFormState$$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly currentFormState$$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private readonly hasUnsavedChanges$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly subscriptionManager$$: Subject<void> = new Subject<void>();

  ngOnDestroy(): void {
    this.subscriptionManager$$.next();
    this.subscriptionManager$$.complete();
  }

  get hasUnsavedChanges$(): Observable<boolean> {
    return this.hasUnsavedChanges$$.asObservable();
  }

  set form(form: FormGroup<T>) {
    this._form = form;
    this.initFormListeners();
  }

  get form(): FormGroup<T> {
    return this._form;
  }

  patchFormValue(
    value: Partial<{
      [K in keyof T]: ɵValue<T[K]>;
    }>,
    options?: {
      onlySelf?: boolean;
      emitEvent?: boolean;
    },
  ): void {
    this.form.patchValue(value, options);
    this.setInitialFormState();
  }

  setInitialFormState(): void {
    const value = JSON.stringify(this.form.getRawValue());
    this.initialFormState$$.next(value);
  }

  private initFormListeners(): void {
    this.subscriptionManager$$.next();

    const initialFormValue: string = JSON.stringify(this._form.getRawValue());

    this.getFormChanges(this._form, initialFormValue)
      .pipe(takeUntil(this.subscriptionManager$$))
      .subscribe((res) => this.currentFormState$$.next(res));

    this.getUnsavedChanges(this.currentFormState$$, this.initialFormState$$)
      .pipe(takeUntil(this.subscriptionManager$$))
      .subscribe((res) => this.hasUnsavedChanges$$.next(res));

    this.initialFormState$$.next(initialFormValue);
  }

  private getFormChanges(form: FormGroup<T>, initialFormValue: string): Observable<string> {
    return form.valueChanges.pipe(
      map((value) => JSON.stringify(value)),
      startWith(initialFormValue),
      distinctUntilChanged(),
    );
  }

  private getUnsavedChanges(
    currentState: Observable<string | null>,
    initialState: Observable<string | null>,
  ): Observable<boolean> {
    return combineLatest([currentState, initialState]).pipe(
      map(([current, initial]) => Boolean(this._form.dirty) && current !== initial),
      distinctUntilChanged(),
    );
  }
}
